import { useState, useRef } from "react";
import { toast } from "sonner";
import { Music2, Loader2, Sparkles, UserCircle2, Coins, Upload, X, FileAudio, Lock, Clapperboard, Share2, Globe, Download } from "lucide-react";
import api, { mediaUrl, downloadUrl } from "@/lib/api";
import { VIDEO_STYLES } from "@/lib/options";
import { useDraft, clearDraft } from "@/lib/useDraft";
import { Chip } from "@/components/Chip";
import StatusBadge from "@/components/StatusBadge";
import ShareDialog from "@/components/ShareDialog";

export const LongVideoPanel = ({ projects, characters, reload }) => {
  const [title, setTitle] = useDraft("mfp_draft_long_title", "");
  const [style, setStyle] = useDraft("mfp_draft_long_style", "cinematic");
  const [characterId, setCharacterId] = useState(null);
  const [quality, setQuality] = useDraft("mfp_draft_long_quality", "pro");
  const [aspect, setAspect] = useDraft("mfp_draft_long_aspect", "9:16");
  const [useScript, setUseScript] = useState(false);
  const [script, setScript] = useDraft("mfp_draft_long_script", "");
  const [audioPath, setAudioPath] = useState(null);
  const [audioName, setAudioName] = useState("");
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [lastId, setLastId] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const fileRef = useRef(null);

  const current = projects.find((p) => p.id === lastId);
  const selectedChar = characters.find((c) => c.id === characterId);

  const togglePublish = async () => {
    if (!current) return;
    setPublishing(true);
    try {
      if (current.published) {
        await api.post(`/projects/${current.id}/unpublish`);
        toast.success("Quitado de la galería");
      } else {
        await api.post(`/projects/${current.id}/publish`, { category: "video" });
        toast.success("Publicado en la galería de la comunidad");
      }
      await reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo actualizar la galería");
    } finally { setPublishing(false); }
  };

  const onPickFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 200 * 1024 * 1024) { toast.error("El audio supera 200MB"); return; }
    setUploading(true);
    setPreview(null);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post("/uploads/audio", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setAudioPath(r.data.path);
      setAudioName(f.name);
      toast.success("Audio subido");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo subir el audio");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const clearAudio = () => { setAudioPath(null); setAudioName(""); setPreview(null); };

  const calcCost = async () => {
    if (!audioPath) { toast.error("Sube tu audio primero"); return; }
    setPreviewing(true);
    try {
      const r = await api.post("/musicvideo/upload/preview", {
        audio_path: audioPath, quality, aspect, style,
      });
      setPreview(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo calcular el costo");
    } finally { setPreviewing(false); }
  };

  const generate = async () => {
    if (!audioPath) { toast.error("Sube tu audio primero"); return; }
    setSubmitting(true);
    try {
      const r = await api.post("/musicvideo/upload?confirm=true", {
        audio_path: audioPath,
        character_id: characterId,
        script: useScript ? script.trim() : "",
        style, quality, aspect,
        title: title.trim() || "Video con tu audio",
      });
      setLastId(r.data.id);
      setPreview(null);
      await reload();
      ["title", "script"].forEach((k) => clearDraft(`mfp_draft_long_${k}`));
      toast.success(`Generando tu video (${r.data.shots} tomas)…`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo generar");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="space-y-6">
      <div className="mfp-card p-6 space-y-6">
        <div>
          <label className="mono-label">Título</label>
          <input data-testid="long-title-input" value={title} onChange={(e) => setTitle(e.target.value)}
            placeholder="Nombre de tu video"
            className="mt-2 w-full bg-[#0B0C13] border border-white/10 rounded-xl px-4 py-3 outline-none focus:border-[#FF334B] transition-colors" />
        </div>

        {/* Audio upload */}
        <div>
          <label className="mono-label">Tu audio (música / voz · hasta 7 min)</label>
          {!audioPath ? (
            <button data-testid="long-audio-upload" onClick={() => fileRef.current?.click()} disabled={uploading}
              className="mt-2 w-full flex items-center justify-center gap-2 border border-dashed border-white/20 hover:border-[#FF334B]/60 rounded-xl px-4 py-6 text-[#94A3B8] transition-colors disabled:opacity-60">
              {uploading ? <Loader2 className="w-5 h-5 spin" /> : <Upload className="w-5 h-5" />}
              {uploading ? "Subiendo…" : "Subir audio (MP3, M4A o WAV)"}
            </button>
          ) : (
            <div className="mt-2 flex items-center justify-between gap-3 rounded-xl border border-[#00F0FF]/30 bg-[#00F0FF]/5 px-4 py-3" data-testid="long-audio-chip">
              <span className="inline-flex items-center gap-2 text-sm text-[#CBD5E1] min-w-0">
                <FileAudio className="w-4 h-4 text-[#00F0FF] shrink-0" />
                <span className="truncate">{audioName}</span>
              </span>
              <button data-testid="long-audio-clear" onClick={clearAudio} className="text-[#64748B] hover:text-[#FF334B] shrink-0"><X className="w-4 h-4" /></button>
            </div>
          )}
          <input ref={fileRef} type="file" accept="audio/*,.mp3,.m4a,.wav,.aac" onChange={onPickFile} className="hidden" />
          <p className="text-xs text-[#64748B] mt-2">La IA cubrirá TODA la duración de tu audio con tomas cinematográficas y lo usará como banda sonora.</p>
        </div>

        {/* Character */}
        <div>
          <label className="mono-label">Personaje (protagonista)</label>
          <div className="mt-3 flex flex-wrap gap-2">
            {characters.map((c) => (
              <button key={c.id} data-testid={`long-character-${c.id}`} onClick={() => setCharacterId(c.id)}
                className={`flex items-center gap-2 pl-1 pr-3.5 py-1 rounded-full border transition-colors ${characterId === c.id ? "bg-[#FF334B] border-[#FF334B] text-white" : "border-white/12 text-[#94A3B8] hover:border-[#FF334B]/50"}`}>
                {c.photo_path ? <img src={mediaUrl(c.photo_path)} alt={c.name} className="w-7 h-7 rounded-full object-cover" /> : <UserCircle2 className="w-7 h-7" />}
                <span className="text-sm font-medium">{c.name}</span>
                {c.locked && <Lock className="w-3 h-3 text-[#FF9F2E]" />}
              </button>
            ))}
          </div>
          {characters.length === 0 && <p className="text-xs text-[#FF9F2E] mt-2">Crea primero un personaje con foto en la pestaña Elenco.</p>}
          <p className="text-xs text-[#64748B] mt-2">El personaje aparecerá ACTUANDO dentro de cada escena (sin que la foto salga en transformación).</p>
        </div>

        {/* Aspect + Quality */}
        <div className="grid sm:grid-cols-2 gap-5">
          <div>
            <label className="mono-label">Formato</label>
            <div className="mt-3 flex gap-2" data-testid="long-aspect">
              <Chip active={aspect === "9:16"} onClick={() => { setAspect("9:16"); setPreview(null); }} testId="long-aspect-vertical">Vertical 9:16</Chip>
              <Chip active={aspect === "16:9"} onClick={() => { setAspect("16:9"); setPreview(null); }} testId="long-aspect-horizontal">Horizontal 16:9</Chip>
            </div>
          </div>
          <div>
            <label className="mono-label">Calidad</label>
            <div className="mt-3 flex gap-2" data-testid="long-quality">
              <Chip active={quality === "standard"} onClick={() => { setQuality("standard"); setPreview(null); }} testId="long-quality-standard">Estándar</Chip>
              <Chip active={quality === "pro"} onClick={() => { setQuality("pro"); setPreview(null); }} testId="long-quality-pro">Pro 1080p</Chip>
            </div>
          </div>
        </div>

        {/* Style */}
        <div>
          <label className="mono-label">Estilo visual</label>
          <div className="mt-3 flex flex-wrap gap-2">
            {VIDEO_STYLES.map((s) => (
              <Chip key={s.id} active={style === s.id} onClick={() => setStyle(s.id)} testId={`long-style-${s.id}`}>{s.label}</Chip>
            ))}
          </div>
        </div>

        {/* Script */}
        <div>
          <button type="button" data-testid="long-script-toggle" onClick={() => setUseScript(!useScript)}
            className={`w-full text-left p-4 rounded-xl border transition-colors ${useScript ? "border-[#FF9F2E] bg-[#FF9F2E]/10" : "border-white/12 hover:border-white/25"}`}>
            <div className="flex items-center justify-between">
              <span className="inline-flex items-center gap-1.5 font-head font-semibold text-sm"><Clapperboard className="w-4 h-4 text-[#FF9F2E]" /> Yo escribo el guion</span>
              <span className={`w-9 h-5 rounded-full flex items-center px-0.5 transition-colors ${useScript ? "bg-[#FF9F2E] justify-end" : "bg-white/15"}`}>
                <span className="w-4 h-4 rounded-full bg-white" />
              </span>
            </div>
            <p className="text-xs text-[#64748B] mt-1">{useScript ? "La IA creará las tomas siguiendo tu historia." : "La IA inventará las tomas automáticamente según el estilo."}</p>
          </button>
          {useScript && (
            <textarea data-testid="long-script-input" value={script} onChange={(e) => setScript(e.target.value)} rows={4}
              placeholder="Cuenta la historia / lo que debe pasar en el video, escena por escena…"
              className="mt-3 w-full bg-[#0B0C13] border border-white/10 rounded-xl px-4 py-3 outline-none focus:border-[#FF9F2E] transition-colors text-sm" />
          )}
        </div>

        <button data-testid="long-calc-cost" onClick={calcCost} disabled={previewing || !audioPath}
          className="w-full inline-flex items-center justify-center gap-2 border border-[#FF9F2E]/50 text-[#FF9F2E] hover:bg-[#FF9F2E]/10 font-semibold py-3 rounded-full transition-colors disabled:opacity-50">
          {previewing ? <Loader2 className="w-5 h-5 spin" /> : <Coins className="w-5 h-5" />}
          Calcular costo exacto
        </button>
      </div>

      {/* Cost preview modal */}
      {preview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="long-cost-modal" onClick={() => setPreview(null)}>
          <div className="mfp-card max-w-md w-full p-6 space-y-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2">
              <Coins className="w-5 h-5 text-[#FF9F2E]" />
              <h3 className="font-head text-xl font-bold">Confirma el costo</h3>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-[#94A3B8]">Duración del audio</span><strong>{preview.audio_seconds}s (~{Math.round(preview.audio_seconds / 60)} min)</strong></div>
              <div className="flex justify-between"><span className="text-[#94A3B8]">Tomas de IA</span><strong data-testid="long-cost-shots">{preview.shots} × {preview.shot_secs}s</strong></div>
              <div className="flex justify-between"><span className="text-[#94A3B8]">Formato · calidad</span><strong>{preview.aspect} · {preview.quality === "pro" ? "Pro 1080p" : "Estándar"}</strong></div>
              <div className="h-px bg-white/10 my-1" />
              <div className="flex justify-between text-base"><span className="text-[#94A3B8]">Costo real en Fal</span><strong className="text-[#FF334B]" data-testid="long-cost-usd">${preview.total_usd} USD</strong></div>
              <p className="text-[11px] text-[#64748B]">Se factura directamente a tu cuenta de Fal (tu llave). Puedes re-ensamblar el video sin costo si ya se generaron las tomas.</p>
            </div>
            <div className="flex gap-3">
              <button data-testid="long-cost-cancel" onClick={() => setPreview(null)}
                className="flex-1 py-3 rounded-full border border-white/15 text-[#94A3B8] hover:text-white transition-colors">Cancelar</button>
              <button data-testid="long-cost-confirm" onClick={generate} disabled={submitting || !audioPath || !preview}
                className="flex-1 inline-flex items-center justify-center gap-2 bg-[#FF334B] hover:bg-[#E0223A] text-white font-semibold py-3 rounded-full transition-colors disabled:opacity-60">
                {submitting ? <Loader2 className="w-5 h-5 spin" /> : <Sparkles className="w-5 h-5" />}
                Confirmar y generar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Result */}
      {current && (
        <div className="mfp-card p-6 space-y-4 fade-up" data-testid="long-result">
          <div className="flex items-center justify-between">
            <div className="font-head font-semibold text-lg">{current.title}</div>
            <StatusBadge status={current.status} />
          </div>
          {(current.status === "processing" || current.status === "queued") && (
            <div className="space-y-2">
              <div className="flex items-center gap-3 text-[#94A3B8]">
                <Loader2 className="w-5 h-5 spin text-[#FF9F2E]" />
                {current.progress?.label || "Generando tus tomas…"} {current.progress?.total ? `(${current.progress.current}/${current.progress.total})` : ""}
              </div>
              <p className="text-xs text-[#64748B]">Un video largo tarda varios minutos (muchas tomas). Puedes salir y volver; seguirá generándose.</p>
            </div>
          )}
          {current.status === "failed" && <p className="text-red-400 text-sm">{current.error}</p>}
          {current.status === "ready" && current.result?.export_path && (
            <video data-testid="long-video-player" src={mediaUrl(current.result.export_path)} controls
              poster={mediaUrl(current.result?.poster_path)}
              className="w-full rounded-xl border border-white/10 bg-black" />
          )}
          {current.status === "ready" && current.result?.export_path && (
            <div className="flex flex-wrap gap-2 pt-1" data-testid="long-result-actions">
              <a data-testid="long-download" href={downloadUrl(current.result.export_path, current.title)}
                className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-full bg-[#FF334B] hover:bg-[#E0223A] text-white transition-colors">
                <Download className="w-4 h-4" /> Descargar
              </a>
              <button data-testid="long-share" onClick={() => setShareOpen(true)}
                className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-full border border-white/15 text-[#CBD5E1] hover:border-[#00F0FF] hover:text-[#00F0FF] transition-colors">
                <Share2 className="w-4 h-4" /> Compartir
              </button>
              <button data-testid="long-publish" onClick={togglePublish} disabled={publishing}
                className={`inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-full border transition-colors disabled:opacity-60 ${current.published ? "border-[#FF9F2E]/50 text-[#FF9F2E] hover:bg-[#FF9F2E]/10" : "border-white/15 text-[#CBD5E1] hover:border-[#FF9F2E] hover:text-[#FF9F2E]"}`}>
                {publishing ? <Loader2 className="w-4 h-4 spin" /> : <Globe className="w-4 h-4" />}
                {current.published ? "Quitar de la galería" : "Publicar en galería"}
              </button>
            </div>
          )}
          {current.status === "ready" && !current.result?.export_path && (
            <div className="flex items-center gap-2 text-sm text-[#FF9F2E]" data-testid="long-finalizing">
              <Loader2 className="w-4 h-4 spin" /> Estamos poniendo tu música al video… recarga en unos segundos. Si persiste, toca Reintentar en Proyectos (ensamblado sin costo).
            </div>
          )}
        </div>
      )}

      <ShareDialog open={shareOpen} onOpenChange={setShareOpen} project={current ? { id: current.id, title: current.title } : null} />
    </div>
  );
};

export default LongVideoPanel;
